import React, { PureComponent } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import Select from "react-select";
import { CSSTransition } from "react-transition-group";
import { Claim, User } from "../../../types";
import DetailView from "../detail-view/index";
import "./Content.css";
import { getStatusBadgeColor } from "../../utils";
import SegmentedControl from "../../components/segmented-control/SegmentedControl";
import ErrorAlert from "../../components/error-alert/ErrorAlert";
import RetryButton from "../../components/error-alert/RetryButton";

type ContentProps = {
  rowData: Claim[];
  user: User;
  requestData: (customerId: number) => any;
};
type ContentState = {
  api: any;
  selectedClaimType: string | null;
  data: Claim[];
  showDetailView: boolean;
  dataSelected: Claim | null;
  selectedSegment: string;
  showSpinner: boolean;
  inputFilter: string;
  showError: boolean;
};

export default class Content extends PureComponent<ContentProps, ContentState> {
  constructor(props: ContentProps) {
    super(props);
    this.state = {
      api: null,
      selectedClaimType: null,
      data: this.props.rowData,
      showDetailView: false,
      dataSelected: null,
      selectedSegment: "my",
      showSpinner: false,
      inputFilter: "",
      showError: false,
    };
  }

  componentDidMount() {
    this.loadClaimData();

    if (this.props.user.role === "CLAIMHANDLER") {
      this.setState({ selectedSegment: "my" });
    } else {
      this.setState({ selectedSegment: "all" });
    }
  }

  componentDidUpdate(prevProps: ContentProps) {
    if (prevProps.rowData !== this.props.rowData) {
      this.setState({ data: this.props.rowData });
    }
  }

  loadClaimData = () => {
    this.setState({ showError: false, showSpinner: true });

    this.props
      .requestData(this.props.user.customerId)
      .catch(() => {
        this.setState({ showError: true });
      })
      .finally(() => {
        this.setState({ showSpinner: false });
      });
  };

  getDefaultColumnDef = () => ({
    sortable: true,
    resizable: true,
    suppressCellSelection: true,
  });

  getStatusBadge = (params: any) => {
    switch (params.value) {
      case "Hold Insured":
        return (
          <span
            className="badge badge-pill"
            style={{
              padding: "0.25rem 0.5rem",
              fontWeight: 500,
              fontSize: "11px",
              color: getStatusBadgeColor(params.value).text,
              backgroundColor: getStatusBadgeColor(params.value).background,
            }}
          >
            {params.value}
          </span>
        );
      case "Hold Cust":
        return (
          <span
            className="badge badge-pill"
            style={{
              padding: "0.25rem 0.5rem",
              fontWeight: 500,
              fontSize: "11px",
              color: getStatusBadgeColor(params.value).text,
              backgroundColor: getStatusBadgeColor(params.value).background,
            }}
          >
            {params.value}
          </span>
        );
      case "In Progress":
        return (
          <span
            className="badge badge-pill"
            style={{
              padding: "0.25rem 0.5rem",
              fontWeight: 500,
              fontSize: "11px",
              color: getStatusBadgeColor(params.value).text,
              backgroundColor: getStatusBadgeColor(params.value).background,
            }}
          >
            {params.value}
          </span>
        );

      case "Re-Opened":
        return (
          <span
            className="badge badge-pill badge-success"
            style={{
              padding: "0.25rem 0.5rem",
              fontWeight: 500,
              fontSize: "11px",
              color: getStatusBadgeColor(params.value).text,
              backgroundColor: getStatusBadgeColor(params.value).background,
            }}
          >
            {params.value}
          </span>
        );
      case "AD Inv Ready":
        return (
          <span
            className="badge badge-pill"
            style={{
              padding: "0.25rem 0.5rem",
              fontWeight: 500,
              fontSize: "11px",
              color: getStatusBadgeColor(params.value).text,
              backgroundColor: getStatusBadgeColor(params.value).background,
            }}
          >
            {params.value}
          </span>
        );
      case "New":
        return (
          <span
            className="badge badge-pill"
            style={{
              padding: "0.25rem 0.5rem",
              fontWeight: 500,
              fontSize: "11px",
              color: getStatusBadgeColor(params.value).text,
              backgroundColor: getStatusBadgeColor(params.value).background,
            }}
          >
            {params.value}
          </span>
        );
      case "PC Complete":
        return (
          <span
            className="badge badge-pill pill-layout"
            style={{
              padding: "0.25rem 0.5rem",
              fontWeight: 500,
              fontSize: "11px",
              color: getStatusBadgeColor(params.value).text,
              backgroundColor: getStatusBadgeColor(params.value).background,
            }}
          >
            {params.value}
          </span>
        );
      default:
        return (
          <span
            className="badge badge-pill badge-secondary"
            style={{
              padding: "0.25rem 0.5rem",
              fontWeight: 500,
              fontSize: "11px",
              color: getStatusBadgeColor(params.value).text,
              backgroundColor: getStatusBadgeColor(params.value).background,
            }}
          >
            {params.value}
          </span>
        );
    }
  };

  getColumnDef = () => [
    {
      headerName: "Date Opened",
      field: "dateOpened",
      cellRendererFramework: (params: any) => (
        <div className="first-column">
          {new Date(params.value).toLocaleDateString("en-NZ")}
        </div>
      ),
      sort: "desc",
    },
    {
      headerName: "Date Closed",
      field: "dateClosed",
      cellRendererFramework: (params: any) => (
        <div className="second-column">
          {params.value
            ? new Date(params.value).toLocaleDateString("en-NZ")
            : ""}
        </div>
      ),
      sort: "desc",
    },
    {
      headerName: "Claim Number",
      field: "claimNumber",
      cellRendererFramework: (params: any) => (
        <div className="third-column">{params.value}</div>
      ),
      sort: "asc",
    },
    {
      headerName: "Insured",
      field: "insuredName",
    },
    {
      headerName: "Street",
      field: "street",
    },
    {
      headerName: "City",
      field: "city",
    },
    {
      headerName: "Status",
      field: "status",
      cellRendererFramework: (params: any) => this.getStatusBadge(params),
    },
    {
      headerName: "Claim Progress",
      field: "claimProgress",
      cellRendererFramework: (params: any) => (
        <span
          className="badge badge-pill"
          style={{
            padding: "0.25rem 0.5rem",
            fontWeight: 500,
            fontSize: "11px",
            color: getStatusBadgeColor(params.value).text,
            backgroundColor: getStatusBadgeColor(params.value).background,
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      headerName: "Type",
      field: "claimType",
    },
    {
      headerName: "Claim Handler",
      field: "claimHandler",
    },
  ];

  /**
   * Cram all columns to fit into the grid initially.
   */
  onGridReady = (params: any) => {
    this.setState({ api: params.api });
    params.api.sizeColumnsToFit();

    if (this.props.user.role === "CLAIMHANDLER") {
      params.api.setQuickFilter(this.props.user.fullname);
    }
    //this.state.api.setQuickFilter(this.state.inputFilter);
  };

  /**
   * Auto-size all columns once the initial data is rendered.
   */
  autoSizeColumns = (params: any) => {
    const colIds = params.columnApi
      .getAllDisplayedColumns()
      .map((col: any) => col.getColId());

    params.columnApi.autoSizeColumns(colIds);
  };

  onFilterTextBoxChanged = (event: any) => {
    this.setState({ inputFilter: event.target.value });
    let quickFilter = event.target.value;
    if (this.state.selectedSegment === "my") {
      quickFilter += " " + this.props.user.fullname;
    }
    this.state.api.setQuickFilter(quickFilter);
  };

  getFilterData = () => {
    const { rowData } = this.props;

    const filterValues = new Map<string, string>(
      rowData.map((item: Claim) => [item.claimType, item.claimTypeDescription])
    );

    const menuItems: { value: string; label: string }[] = [];
    filterValues.forEach((value, key, map) => {
      menuItems.push({ value: key, label: value });
    });
    return menuItems;
  };

  handleMyClaimsSwitchChange = (segment: string) => {
    if (this.state.selectedSegment === segment) {
      return;
    }

    let quickFilter = this.state.inputFilter;

    switch (segment) {
      case "my":
        quickFilter += " " + this.props.user.fullname;
        break;
      case "all":
      default:
        this.state.api.setQuickFilter("");
        break;
    }

    this.state.api.setQuickFilter(quickFilter);
    this.setState({ selectedSegment: segment });
  };

  handleChange = (selectedClaimTypes: any) => {
    let newData;
    if (selectedClaimTypes && selectedClaimTypes.length > 0) {
      newData = this.props.rowData.filter((item: Claim) =>
        selectedClaimTypes
          .map((claim: { value: string; label: string }) => claim.value)
          .includes(item.claimType)
      );
    } else {
      newData = this.props.rowData;
    }

    this.setState({ data: newData });
  };

  onCellFocused = (event: any) => {
    const { api, showDetailView } = this.state;
    if (api) {
      /* Select the rows as they are focused (triggering onRowSelectionChanged) */
      if (api.getModel().getRow(event.rowIndex)) {
        api.getModel().getRow(event.rowIndex).setSelected(true);
        if (showDetailView === false) {
          this.selectRow();
        }
      }
    }
  };

  selectRow = () => {
    const { api } = this.state;
    if (api) {
      const selectedRows = api.getSelectedRows();
      if (selectedRows && selectedRows.length > 0) {
        this.setState(
          { dataSelected: selectedRows[0], showDetailView: false },
          () => this.setState({ showDetailView: true })
        );
      }
    }
  };

  closeDetailView = () => {
    this.setState({ showDetailView: false });
  };

  render() {
    const { showDetailView, data, dataSelected, showSpinner, showError } =
      this.state;

    const { user } = this.props;
    const colDef = this.getColumnDef();

    if (user.customerId === -1) {
      colDef.push({
        headerName: "Customer",
        field: "customer",
      });
    }
    return (
      <div
        style={{
          display: "flex",
          padding: "1rem",
          width: "100%",
          height: "100%",
        }}
      >
        <div className="content-wrapper">
          <div className="datagrid-wrapper">
            <div className="datagrid-header">
              <div
                style={{
                  display: "flex",
                  flex: "1 1 400px",
                  flexWrap: "wrap",
                }}
              >
                <input
                  className="content-filter"
                  type="search"
                  placeholder="Search for Claim..."
                  onChange={(e) => this.onFilterTextBoxChanged(e)}
                />
                <Select
                  options={this.getFilterData()}
                  placeholder="Claim Type..."
                  isClearable
                  onChange={(e: any) => this.handleChange(e)}
                  className="claim-type"
                  isMulti
                />
              </div>
              <div style={{ display: "flex", marginBottom: "0.25rem" }}>
                {this.props.user.role === "CLAIMHANDLER" && (
                  <SegmentedControl
                    onChange={this.handleMyClaimsSwitchChange}
                    keyOptionOne="my"
                    labelOptionOne="My Claims"
                    keyOptionTwo="all"
                    labelOptionTwo="All Claims"
                  />
                )}
              </div>
            </div>
            {showSpinner && (
              <div
                style={{
                  display: "flex",
                  flex: "1 1 auto",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="spinner-border spinner-border-lg" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}
            {showError && !showSpinner && (
              <div
                className="content-data"
                style={{
                  display: "flex",
                  flex: "1 1 auto",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ErrorAlert lightMode>
                  <span style={{ paddingRight: "1rem" }}>
                    Error while loading your claims. Please retry or submit a
                    Support Desk ticket.
                  </span>
                  <RetryButton retryFunction={this.loadClaimData} />
                </ErrorAlert>
              </div>
            )}
            {!showError && !showSpinner && (
              <div
                className="ag-theme-balham"
                style={{ width: "100%", height: "94%" }}
              >
                <AgGridReact
                  paginationAutoPageSize={true}
                  defaultColDef={this.getDefaultColumnDef()}
                  pagination={true}
                  suppressColumnVirtualisation={true}
                  onGridReady={this.onGridReady}
                  onFirstDataRendered={this.autoSizeColumns}
                  onRowSelected={this.selectRow}
                  onCellFocused={this.onCellFocused}
                  columnDefs={colDef}
                  rowData={data}
                ></AgGridReact>
              </div>
            )}
          </div>
        </div>

        <CSSTransition
          in={showDetailView}
          timeout={500}
          classNames="detail-view"
          unmountOnExit
        >
          {(dataSelected && (
            <DetailView
              dataSelected={dataSelected}
              closeDetailView={this.closeDetailView}
            />
          )) || <></>}
        </CSSTransition>
      </div>
    );
  }
}
